import {
  take,
  takeLatest,
  fork,
  call,
  select,
  put,
  cancel,
  race,
  all,
} from 'redux-saga/effects'
import { delay } from 'redux-saga'
import { isEmpty, get } from 'lodash-es'

import { RealtimeClient } from '../../../sharedLegacy/utils'
import {
  getAudienceForObject,
  getAudienceList,
  getAudienceRealtimeData,
} from '../../api'
import createNamedSubscriptionSagas from '../sagas.subscriptions'
import { showNotice } from '../../../Layout/Notices/Notices'

import createNamedActions from './actions'

const PAGE_SIZE = 101

const realtimeClient = new RealtimeClient()
let realtimeChannelName

const isTabDataLoaded = (state, namespace, tab, objectId) => {
  const tabData = get(state, `${namespace}.${tab}`)
  const isSameContext = get(state, `${namespace}.objectId`) === objectId

  if (tabData && isSameContext) {
    return !tabData.isEmpty()
  }

  return false
}

const getCurrentOffset = (state, namespace, tab) =>
  state[namespace].currentOffset[tab]
const getCurrentTab = (state, namespace) => state[namespace].selectedTab
const getObjectId = (state, namespace) => state[namespace].objectId
const getOrderBy = (state, namespace, tab) => state[namespace].orderBy[tab]
const getOrder = (state, namespace, tab) => state[namespace].order[tab]

function* getOrderFilters(namespace, tab) {
  const orderBy = yield select(getOrderBy, namespace, tab)
  const order = yield select(getOrder, namespace, tab)
  return {
    ...(!isEmpty(orderBy) ? { orderby: orderBy } : {}),
    ...(!isEmpty(order) ? { order } : {}),
  }
}

const webinarsParams = {
  objects: undefined, // reset prop
  only_recordings_p: undefined, // reset prop
  resolve_recordings_p: 1,
  orderby: 'attended_type', // this is the default value, it will be overwritten when order changes
}

const getPredefinedFilters = (tab, objectId) => {
  if (tab === 'identified') {
    return { identified: true }
  }

  if (tab === 'converted') {
    return {
      objects: undefined,
      identity_objects: objectId,
    }
  }

  // webinar one
  if (tab === 'attendees') {
    return {
      ...webinarsParams,
      timeline_objects: objectId,
      identity_objects: objectId,
    }
  }

  // webinar one
  if (tab === 'absentees') {
    return {
      ...webinarsParams,
      not_timeline_objects: objectId,
      identity_objects: objectId,
    }
  }

  // webinar one
  if (tab === 'webinarEverybody') {
    return {
      ...webinarsParams,
      identity_objects: objectId,
    }
  }

  // webinar one
  if (tab === 'liveAttendees') {
    return {
      ...webinarsParams,
      timeline_objects: objectId,
      identity_objects: objectId,
      resolve_recordings_p: 0,
    }
  }

  // webinar one
  if (tab === 'onDemandAttendees') {
    return {
      ...webinarsParams,
      timeline_objects: objectId,
      identity_objects: objectId,
      only_recordings_p: 1,
    }
  }

  return {}
}

const addOptionsToFilters = (objectId, options = {}) => ({
  ...(options.includeEvents ? { include_events_p: 1 } : {}),
  ...(options.expireProfiles ? { timeline_objects: objectId } : {}),
})

function* getFilters(namespace, tab, objectId, options) {
  const predefinedFilters = getPredefinedFilters(tab, objectId)
  const optionFilters = addOptionsToFilters(objectId, options)
  const orderFilters = yield call(getOrderFilters, namespace, tab)
  const offset = yield select(getCurrentOffset, namespace, tab)

  return {
    ...predefinedFilters,
    ...orderFilters, // overwrite the predefined Filters
    ...optionFilters,
    offset,
  }
}

function* fetchAudience(
  context,
  tab,
  objectId,
  options = {},
  shouldRefetch = false
) {
  const { actionNames, namespace } = context
  const filters = yield call(getFilters, namespace, tab, objectId, options)
  let callParams

  if (
    !shouldRefetch &&
    (yield select(isTabDataLoaded, namespace, tab, objectId))
  ) {
    return
  }

  try {
    if (objectId) {
      callParams = [getAudienceForObject, objectId, filters]
    } else {
      callParams = [getAudienceList, filters]
    }

    const { audience, timeout } = yield race({
      timeout: delay(2500),
      audience: yield call(...callParams),
    })

    if (timeout) {
      yield put({ type: actionNames.INSET_AUDIENCE_FETCH_FAILURE, tab })
    } else {
      yield put({
        type: actionNames.INSET_AUDIENCE_FETCH_SUCCESS,
        tab,
        data: audience,
      })
    }
  } catch (err) {
    yield put({ type: actionNames.INSET_AUDIENCE_FETCH_FAILURE, tab })
  }
}

function* watchInfiniteScroll(actionNames, namespace, options) {
  yield take(actionNames.INSET_AUDIENCE_FETCH_SUCCESS)

  yield takeLatest(
    actionNames.INSET_INFINITE_LOAD,
    function* onInfiniteLoad(action) {
      const { tab, objectId } = action
      const filters = yield call(getFilters, namespace, tab, objectId, options)
      const currentOffset = yield select(getCurrentOffset, namespace, tab)
      const newOffset = currentOffset + PAGE_SIZE
      const filtersWithNewOffset = { ...filters, offset: newOffset }
      let audience

      try {
        if (objectId) {
          audience = yield call(
            getAudienceForObject,
            objectId,
            filtersWithNewOffset
          )
        } else {
          audience = yield call(getAudienceList, filtersWithNewOffset)
        }

        yield put({
          type: actionNames.INSET_INFINITE_LOAD_SUCCESS,
          tab,
          data: audience,
          newOffset,
        })
      } catch (err) {
        showNotice({
          message: 'Additional profiles could not be loaded.',
          expirationMillis: 5000,
          isClosable: true,
        })
        yield put({ type: actionNames.INSET_INFINITE_LOAD_ERROR, tab })
      }
    }
  )
}

function* subscribeToRealtimeUpdates(sagaChannel, actionNames) {
  while (true) {
    const realtimeData = yield take(sagaChannel)

    yield put({ type: actionNames.INSET_REALTIME_DATA_UPDATE, realtimeData })
  }
}

function* setupRealtime(actionNames, options = {}) {
  if (options.isRealtimeDisabled) {
    return
  }

  try {
    const { realtime } = yield call(getAudienceRealtimeData)

    /* Dsiabled the camelcase rule, in this testing scenario */
    const {
      realtime_url,
      realtime_authorization,
      realtime_channel: realtimeChannel,
    } = realtime

    yield call(realtimeClient.connect, realtime_url, realtime_authorization)
    realtimeChannelName = realtimeChannel
    const sagaChannel = yield call(
      realtimeClient.subscribeThroughSagaChannel,
      realtimeChannel
    )

    yield fork(subscribeToRealtimeUpdates, sagaChannel, actionNames)
  } catch (err) {
    showNotice({
      message: 'Real time updates have been disabled due to an error.',
      expirationMillis: 5000,
      isClosable: true,
    })
  }
}

function* watchInit(actionNames, namespace, options = {}, action = undefined) {
  const { watchFollowing } = createNamedSubscriptionSagas(namespace)

  if (options.enableSubscriptions) {
    yield fork(watchFollowing)
  }

  yield fork(
    fetchAudience,
    { namespace, actionNames },
    action.tab,
    action.objectId,
    options
  )
}

function* refetchProfile(offset, actionNames, namespace, options) {
  try {
    const tab = yield select(getCurrentTab, namespace)
    const objectId = yield select(getObjectId, namespace)
    const filters = yield call(getFilters, namespace, tab, objectId, options)
    const filtersWithNewOffset = { ...filters, offset }
    let audience

    if (objectId) {
      audience = yield call(
        getAudienceForObject,
        objectId,
        filtersWithNewOffset
      )
    } else {
      audience = yield call(getAudienceList, filtersWithNewOffset)
    }

    yield put({
      type: actionNames.INSET_PROFILES_REFETCH_SUCCESS,
      tab,
      data: audience,
      offset,
    })
  } catch (err) {
    showNotice({
      message: 'Online status of some audience profiles could not be updated',
      expirationMillis: 5000,
      isClosable: true,
    })
  }
}

function* refetchProfiles(actionNames, namespace, options) {
  const tab = yield select(getCurrentTab, namespace)
  const offset = yield select(getCurrentOffset, namespace, tab)

  // fetch two batches of 100 profiles in parallel
  for (let i = 0; i <= offset; i += 200) {
    const req1 = call(refetchProfile, i, actionNames, namespace, options)
    let req2 = Promise.resolve()

    if (i + 100 <= offset) {
      req2 = call(refetchProfile, i + 100, actionNames, namespace, options)
    }

    yield all([req1, req2])
  }
}

function* watchProfileExpiration(actionNames, namespace, options) {
  while (true) {
    yield delay(90000)
    yield fork(refetchProfiles, actionNames, namespace, options)
  }
}

function* watchTabRouteChange(actionNames, namespace, options) {
  const actionName = actionNames.INSET_TAB_ROUTE_CHANGE

  yield takeLatest(
    actionName,
    function* onTabRouteChange({ objectId, tab = 'everybody' }) {
      yield fork(
        fetchAudience,
        { namespace, actionNames },
        tab,
        objectId,
        options
      )
    }
  )
}

function* watchOrderChange(actionNames, namespace, options) {
  yield takeLatest(actionNames.INSET_ORDER_CHANGE, function* onOrderChange() {
    const tab = yield select(getCurrentTab, namespace)
    const objectId = yield select(getObjectId, namespace)
    const shouldRefetch = true

    yield fork(
      fetchAudience,
      { namespace, actionNames },
      tab,
      objectId,
      options,
      shouldRefetch
    )
  })
}

function cleanupRealtime() {
  realtimeClient.cancelSubscription(realtimeChannelName)
  realtimeChannelName = null
}

function* watchLeave(action, tasks) {
  yield take(action)
  yield tasks.map((task) => cancel(task))
  yield call(cleanupRealtime)
}

function* init(actionNames, namespace, options) {
  const tasks = []

  while (true) {
    const action = yield take(actionNames.INSET_INIT)
    tasks.push(yield fork(watchInit, actionNames, namespace, options, action))
    tasks.push(yield fork(setupRealtime, actionNames, options))
    tasks.push(yield fork(watchTabRouteChange, actionNames, namespace, options))
    tasks.push(yield fork(watchInfiniteScroll, actionNames, namespace, options))
    tasks.push(yield fork(watchOrderChange, actionNames, namespace, options))

    if (options.expireProfiles) {
      tasks.push(
        yield fork(watchProfileExpiration, actionNames, namespace, options)
      )
    }

    yield call(watchLeave, actionNames.INSET_LEAVE, tasks)
  }
}

const createNamedSagas = (namespace, baseURL, options = {}) => {
  const { actionNames } = createNamedActions(namespace)
  return [() => init(actionNames, namespace, options)]
}

export { watchInit, setupRealtime, watchTabRouteChange, watchInfiniteScroll }
export default createNamedSagas
